// src/components/principal/SubscriptionPanel.jsx (v3 – config-driven, clean)

import React, { useEffect, useState } from "react";

import useSubscription, { formatLocalDate } from "../../hooks/useSubscription";
import useStripeActions from "../../hooks/useStripeActions";
import { STRIPE_PUBLISHABLE_KEY, DEFAULT_PRICE_ID } from "../../config";

/**
 * Subscription management panel for principals.
 *
 * Displays current status (trialing, active, canceling, inactive)
 * and provides actions to subscribe or manage via Stripe Customer Portal.
 *
 * @component
 * @returns {JSX.Element}
 */
const SubscriptionPanel = () => {
  const { status, meta, refresh } = useSubscription({ enabled: true });

  const { startSubscription, openCustomerPortal, loading } = useStripeActions({
    publishableKey: STRIPE_PUBLISHABLE_KEY,
    priceId: DEFAULT_PRICE_ID,
  });

  const [showStripeOverlay, setShowStripeOverlay] = useState(false);
  const [localError, setLocalError] = useState("");

  // Show overlay only after brief delay
  useEffect(() => {
    if (!loading) {
      setShowStripeOverlay(false);
      return;
    }
    const id = setTimeout(() => setShowStripeOverlay(true), 400);
    return () => clearTimeout(id);
  }, [loading]);

  const canUsePortal = Boolean(meta?.stripe_subscription_id);

  const handleStartSubscription = async () => {
    setLocalError("");
    try {
      await startSubscription();
      await refresh();
    } catch (err) {
      console.error("Subscription start error:", err);
      setLocalError(
        err?.response?.data?.detail ||
          err?.message ||
          "Unable to start subscription. Please try again."
      );
    }
  };

  const handleOpenPortal = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLocalError("");
    try {
      await openCustomerPortal();
    } catch (err) {
      console.error("Portal open error:", err);
      setLocalError(
        err?.response?.data?.detail ||
          err?.message ||
          "Unable to open Customer Portal. Please try again."
      );
    }
  };

  const PortalLinkButton = ({ children }) => (
    <button type="button" onClick={handleOpenPortal} className="link-button">
      {children}
    </button>
  );

  const StripeOverlay = ({ visible }) =>
    !visible ? null : (
      <div className="stripe-overlay">
        <div className="stripe-overlay-backdrop" />
        <div className="stripe-overlay-content">
          <p>Connecting securely to Stripe…</p>
        </div>
      </div>
    );

  // === TRIALING ===
  if (status === "TRIALING") {
    return (
      <div className="principal-panel subscription-content">
        <h3>Manage Subscription</h3>
        {canUsePortal ? (
          <p>
            Your 7-day free trial is active. Complete payment in the{" "}
            <PortalLinkButton>Customer Portal</PortalLinkButton> anytime before
            it ends to keep your account and data.
          </p>
        ) : (
          <>
            <p className="no-subscription-message">
              Your school is in a 7-day free trial. Subscribe now to keep access
              and data after the trial, or continue and subscribe later.
            </p>
            <button
              onClick={handleStartSubscription}
              disabled={loading}
              className="btn btn-primary subscription-button"
            >
              Exit Trial and Subscribe
            </button>
          </>
        )}

        {localError && <p className="error-message">{localError}</p>}
        <StripeOverlay visible={showStripeOverlay} />
      </div>
    );
  }

  // === ACTIVE (not canceling) ===
  if (status === "ACTIVE" && !meta?.subscription_cancel_at_period_end) {
    return (
      <div className="principal-panel subscription-content">
        <h3>Manage Subscription</h3>
        {canUsePortal ? (
          <p>
            Your subscription is active. Manage it in the{" "}
            <PortalLinkButton>Customer Portal</PortalLinkButton>.
          </p>
        ) : (
          <>
            <p>Your subscription is active.</p>
            <button
              onClick={handleStartSubscription}
              disabled={loading}
              className="btn btn-primary subscription-button"
            >
              Update Billing
            </button>
          </>
        )}

        {localError && <p className="error-message">{localError}</p>}
        <StripeOverlay visible={showStripeOverlay} />
      </div>
    );
  }

  // === ACTIVE (canceling) ===
  if (status === "ACTIVE" && meta?.subscription_cancel_at_period_end) {
    const endISO =
      meta.subscription_current_period_end_iso ||
      meta.subscription_cancel_at_iso ||
      null;

    return (
      <div className="principal-panel subscription-content">
        <h3>Manage Subscription</h3>
        <p>
          <strong>Canceling:</strong> Your subscription ends on{" "}
          <strong>{formatLocalDate(endISO)}</strong>. Change this in the{" "}
          {canUsePortal ? (
            <PortalLinkButton>Customer Portal</PortalLinkButton>
          ) : (
            "Customer Portal"
          )}
          .
        </p>

        {localError && <p className="error-message">{localError}</p>}
        <StripeOverlay visible={showStripeOverlay} />
      </div>
    );
  }

  // === INACTIVE or default ===
  return (
    <div className="principal-panel subscription-content">
      <h3>Manage Subscription</h3>
      <p className="no-subscription-message">
        No active subscription. Subscribe to enable staff uploads and keep your
        school’s accounts and data.
      </p>
      <button
        onClick={handleStartSubscription}
        disabled={loading}
        className="btn btn-primary subscription-button"
      >
        Subscribe to GhrianGhraffy
      </button>

      {localError && <p className="error-message">{localError}</p>}
      <StripeOverlay visible={showStripeOverlay} />
    </div>
  );
};

export default SubscriptionPanel;